import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import enUS from "./en-US/common.json";
import esES from "./es-ES/common.json";
import enAppointments from "./en-US/appointments.json";
import esAppointments from "./es-ES/appointments.json";
import enClients from "./en-US/clients.json";
import esClients from "./es-ES/clients.json";
import enMessaging from "./en-US/messaging.json";
import esMessaging from "./es-ES/messaging.json";
import enPublic from "./en-US/public.json";
import esPublic from "./es-ES/public.json";
import enSettings from "./en-US/settings.json";
import esSettings from "./es-ES/settings.json";
import enBooking from "./en-US/booking.json";
import esBooking from "./es-ES/booking.json";
import enPos from "./en-US/pos.json";
import esPos from "./es-ES/pos.json";
import enTeam from "./en-US/team.json";
import esTeam from "./es-ES/team.json";
import enFinance from "./en-US/finance.json";
import esFinance from "./es-ES/finance.json";
import enDashboard from "./en-US/dashboard.json";
import esDashboard from "./es-ES/dashboard.json";
import enEvents from "./en-US/events.json";
import esEvents from "./es-ES/events.json";
import enConsent from "./en-US/consent.json";
import esConsent from "./es-ES/consent.json";
import enApprenticeship from "./en-US/apprenticeship.json";
import esApprenticeship from "./es-ES/apprenticeship.json";
import enShop from "./en-US/shop.json";
import esShop from "./es-ES/shop.json";
import enInventory from "./en-US/inventory.json";
import esInventory from "./es-ES/inventory.json";
import enMarketing from "./en-US/marketing.json";
import esMarketing from "./es-ES/marketing.json";
import enSystem from "./en-US/system.json";
import esSystem from "./es-ES/system.json";
import enDemo from "./en-US/demo.json";
import esDemo from "./es-ES/demo.json";

import { detectRegion, getStoredLanguage, toSupportedLanguage, type SupportedLanguage } from "@/lib/region";

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = ["en-US", "es-ES"];

/**
 * Detection order:
 *   1. explicit choice saved by the LanguageSwitcher (localStorage)
 *   2. visitor region: Spain (GeoIP cookie / timezone) ⇒ es-ES
 *   3. browser language (es-* ⇒ es-ES, en-* ⇒ en-US)
 * The shop's country can still override 2–3 after login (see LocaleContext),
 * but never an explicit choice.
 */
const detector = new LanguageDetector();
detector.addDetector({
  name: "shopledgerRegion",
  lookup: () => {
    const stored = getStoredLanguage();
    if (stored) return stored;
    return detectRegion().language;
  },
  cacheUserLanguage: () => {
    /* explicit choices are written by setStoredLanguage(); never auto-cache */
  },
});

i18n
  .use(detector)
  .use(initReactI18next)
  .init({
    resources: {
      "en-US": {
        common: enUS,
        appointments: enAppointments,
        clients: enClients,
        messaging: enMessaging,
        public: enPublic,
        settings: enSettings,
        booking: enBooking,
        pos: enPos,
        team: enTeam,
        finance: enFinance,
        dashboard: enDashboard,
        events: enEvents,
        consent: enConsent,
        apprenticeship: enApprenticeship,
        shop: enShop,
        inventory: enInventory,
        marketing: enMarketing,
        system: enSystem,
        demo: enDemo,
      },
      "es-ES": {
        common: esES,
        appointments: esAppointments,
        clients: esClients,
        messaging: esMessaging,
        public: esPublic,
        settings: esSettings,
        booking: esBooking,
        pos: esPos,
        team: esTeam,
        finance: esFinance,
        dashboard: esDashboard,
        events: esEvents,
        consent: esConsent,
        apprenticeship: esApprenticeship,
        shop: esShop,
        inventory: esInventory,
        marketing: esMarketing,
        system: esSystem,
        demo: esDemo,
      },
    },
    ns: ["common", "appointments", "clients", "messaging", "public", "settings", "booking", "pos", "team", "finance", "dashboard", "events", "consent", "apprenticeship", "shop", "inventory", "marketing", "system", "demo"],
    defaultNS: "common",
    supportedLngs: SUPPORTED_LANGUAGES,
    load: "currentOnly",
    fallbackLng: "en-US",
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ["shopledgerRegion"],
      caches: [],
    },
  });

/** Keep <html lang> in sync so screen readers, fonts and hyphenation follow the UI language. */
function syncDocumentLanguage(lng: string | undefined) {
  if (typeof document === "undefined") return;
  const supported = toSupportedLanguage(lng) ?? "en-US";
  document.documentElement.lang = supported === "es-ES" ? "es" : "en";
}
syncDocumentLanguage(i18n.language);
i18n.on("languageChanged", syncDocumentLanguage);

export default i18n;
